import { mq } from '@/utils/mediaQuery';
import { CALENDER_MODAL_ZINDEX } from './constants/zindex';
import { changePrimaryFontFamily, changeSecondaryFontFamily } from '@/utils/functions';

export const image = {
  arrowLeft: require('@/assets/images/arrow-left.svg'),
  arrowRightPrimary: require('@/assets/images/arrow-right-primary.svg'),
  arrowRight: require('@/assets/images/arrow-right.svg'),
  arrowDown: require('@/assets/images/arrowDown.png'),
  close: require('@/assets/images/close.svg'),
  closeIcon: require('@/assets/images/closeIcon.svg'),
  datepickerIcon: require('@/assets/images/datepickerIcon.svg'),
  fileUploadIcon: require('@/assets/images/file-upload-icon.svg'),
  footerBackgroundMobile: require('@/assets/images/footer-background-mobile.png'),
  footerBackground: require('@/assets/images/footer-background.png'),
  landingArena: require('@/assets/images/landing-arena.png'),
  landingMice: require('@/assets/images/landing-mice.png'),
  logoHeader: require('@/assets/images/logoHeader.png'),
  logoWhiteFull: require('@/assets/images/logoWhiteFull.png'),
  modalCloseIcon: require('@/assets/images/modal-close-icon.svg'),
};

export const color = {
  white: '#ffffff',
  light_grey: '#CDCCCC',
  primary_color: '#6F5C80',
  base_white: '#ffffff',
  base_black: '#222222',
  point03: '#9C7BB7',
  point04: '#E7AF1B',
  grey03: '#CDCCCC',
  base_red: '#d32f2f',
  base_pink: '#f3e5ff',
  base_purple: '#68507C',
  base_gray: '#BDBDBD',
  base_blue: '#7C8FAC',
};

export const AppTheme = {
  image,
  color,
  fontFamily: {
    primaryFont: changePrimaryFontFamily(),
    secondaryFont: changeSecondaryFontFamily(),
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    bold: 700,
    extraBold: 800,
  },
  palette: {
    primary: {
      main: '#68507C',
    },
  },
  shadow: {},
  typography: {
    fontFamily: changePrimaryFontFamily(),
    htmlFontSize: 10,

    [mq('mobile')]: {
      fontSize: 14,
    },

    [mq('desktop')]: {
      fontSize: 16,
    },
  },
  // NOTE: Custom Mui
  components: {
    MuiFormControl: {
      styleOverrides: {
        root: () => ({
          width: '100%',
        }),
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: ({ theme }) => ({
          top: '6rem !important',

          '& .MuiAlert-root': {
            background: theme.color.base_blue,
            borderRadius: '1rem',
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          ...(ownerState.variant === 'contained' && {
            fontFamily: theme.fontFamily.sCoreDream4Regular,
            fontSize: '1.7rem',
            textAlign: 'center',
            boxShadow: theme.shadow.primary,
            color: theme.color.base_black,

            ':hover': {
              boxShadow: theme.shadow.secondary,
              color: theme.color.base_black,
            },
          }),
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          width: '100%',
          fontSize: '1.6rem',
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: theme.fontFamily.primaryFont,

          '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: '0.8rem',
          },

          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: `2px solid ${theme.color.base_purple}`,
          },

          '&.Mui-focused': {
            color: color.base_black,
          },
        }),
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: theme.fontFamily.primaryFont,
          fontSize: '1.6rem',
          '&.Mui-focused': {
            color: theme.color.base_purple,
          },
        }),
        asterisk: {
          color: color.base_red,
          fontSize: '1.6rem',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '2.5rem',
        },
      },
    },

    MuiPickersPopper: {
      styleOverrides: {
        root: () => ({
          zIndex: CALENDER_MODAL_ZINDEX,
        }),
      },
    },

    MuiPickersToolbar: {
      styleOverrides: {
        root: {
          display: 'none',
        },
      },
    },

    MuiDateCalendar: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: '32rem',
          maxHeight: 'none',
          paddingBottom: '2rem',
          height: 'auto',

          '.MuiDateCalendar-viewTransitionContainer': {
            display: 'flex',
            justifyContent: 'center',
          },

          '.MuiDayCalendar-slideTransition': {
            minHeight: '24rem',
          },

          [mq('desktop')]: {
            width: '36rem',
            borderRadius: '2.5rem',
            boxShadow: theme.shadow.primary,
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: () => ({
          minHeight: '5.6rem',
          fontSize: '1.4rem !important',
        }),
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontWeight: theme.fontWeight.bold,
          borderRadius: 5,
          borderWidth: 0,
          border: '0',

          '&:hover': {
            backgroundColor: `${theme.color.base_purple} !important`,
            color: `${theme.color.base_white} !important`,
          },

          '&.Mui-selected': {
            fontWeight: theme.fontWeight.bold,
            backgroundColor: `${theme.color.base_purple} !important`,
            color: `${theme.color.base_white} !important`,
          },

          '&.MuiPickersDay-today': {
            position: 'relative',
            border: 0,
            background: 'transparent',

            ':after': {
              position: 'absolute',
              content: '""',
              bottom: '0.4rem',
              left: '50%',
              transform: 'translate(-50%, 0)',
              width: '1.8rem',
              height: '0.2rem',
              borderRadius: '0.2rem',
              background: theme.color.base_purple,
            },
          },
        }),

        today: {
          borderRadius: 5,
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          fontWeight: 700,
          color: '#222222',
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: ({ theme }) => ({
          justifyContent: 'center',
          textAlign: 'center',
          margin: '2.4rem 0 2.4rem 3rem',

          '.MuiPickersCalendarHeader-labelContainer': {
            marginRight: 0,
          },

          '.MuiPickersArrowSwitcher-root': {
            '.MuiIconButton-edgeEnd': {
              left: 0,
              top: '45%',
              position: 'absolute',
              zIndex: '999',
              color: theme.color.base_black,
            },

            '.MuiIconButton-edgeStart': {
              position: 'absolute',
              right: 0,
              top: '45%',
              zIndex: '999',
              color: theme.color.base_black,
            },
          },
        }),

        label: {
          color: '#222',
          fontWeight: 700,
          textTransform: 'uppercase',
          fontSize: '2rem',
        },
      },
    },
  },
};

export default AppTheme;
